import { toast } from "react-toastify";

/**
 * Payment error codes. The API returns them as the body of a 400.
 */
export const PaymentError = {
  /** The stored billing address is missing one of the seven fields the invoice needs. */
  BillingAddressInvalid: "BILLING_ADDRESS_INVALID",
  /** A live subscription already exists -- normally just a stale UI. */
  UserAlreadySubscribed: "USER_ALREADY_SUBSCRIBED",
  /** The user has not accepted a terms version. */
  TermsVersionNotSpecified: "TERMS_VERSION_NOT_SPECIFIED",
  /** The plan is not mapped to a provider price in this environment. Our fault, not theirs. */
  SubscriptionTypeNotConfigured: "SUBSCRIPTION_TYPE_NOT_CONFIGURED",
  /** An Enterprise licence -- subscriptions do not apply. */
  UserLicenseInvalid: "USER_LICENSE_INVALID",
  /**
   * Tried to change the plan of a subscription already set to end. It would end before a
   * repriced item ever renewed, so the customer would see the new plan everywhere except on the
   * invoice that never arrives. Resume first -- resuming can change the plan in the same step.
   */
  SubscriptionCancellationPending: "SUBSCRIPTION_CANCELLATION_PENDING",
  /**
   * Month <-> Year is blocked as a stopgap. Stripe cannot defer this specific price change to
   * the next renewal without a Subscription Schedule (not yet built), so allowing it gives away
   * up to a year of service for free. See docs/stripe-migration-plan.md, "Further Stripe
   * improvements", in the backend repository.
   */
  SubscriptionIntervalChangeNotSupported:
    "SUBSCRIPTION_INTERVAL_CHANGE_NOT_SUPPORTED",
};

/**
 * Codes that mean "send the user to the billing form" rather than "show an error".
 */
export const requiresBillingDetails = (errorCode) =>
  errorCode === PaymentError.BillingAddressInvalid ||
  errorCode === PaymentError.TermsVersionNotSpecified;

/**
 * Codes that are not really errors from the user's point of view -- the UI was working from
 * stale state, and a refresh resolves it by re-rendering the right action. Nothing should be
 * shouted at the user for these.
 */
export const isStaleStateError = (errorCode) =>
  errorCode === PaymentError.UserAlreadySubscribed ||
  errorCode === PaymentError.SubscriptionCancellationPending;

/**
 * Danish message for a payment error code, or null when the caller should handle the code
 * itself instead of showing a message.
 */
export const paymentErrorMessage = (errorCode) => {
  switch (errorCode) {
    case PaymentError.UserLicenseInvalid:
      return "Din licens administreres af NemEkstrakt. Kontakt os for at ændre dit abonnement.";
    case PaymentError.SubscriptionTypeNotConfigured:
      return "Abonnementet kan ikke oprettes lige nu. Prøv igen senere, eller kontakt support.";
    case PaymentError.SubscriptionIntervalChangeNotSupported:
      return "Skift mellem månedlig og årlig betaling er midlertidigt ikke muligt. Kontakt support, hvis du ønsker at skifte abonnementstype.";
    case PaymentError.BillingAddressInvalid:
    case PaymentError.TermsVersionNotSpecified:
    case PaymentError.UserAlreadySubscribed:
    case PaymentError.SubscriptionCancellationPending:
      return null;
    default:
      return "Betalingen kunne ikke startes. Prøv igen, eller kontakt support.";
  }
};

/**
 * Shows the customer the toast a failed payment call deserves, if any.
 *
 * The two silent cases are deliberate, and both are silent for a reason rather than by omission:
 * a stale-state code means the UI was working from old state and the caller's refresh re-renders
 * the right action, and a billing-details code is answered by navigating to the form. Callers
 * handle the second themselves via `requiresBillingDetails`, because only they know where their
 * form is.
 */
export const reportPaymentError = (errorCode) => {
  if (isStaleStateError(errorCode) === true) {
    return;
  }

  const message = paymentErrorMessage(errorCode);
  if (message) {
    toast.error(message);
  }
};

/**
 * The message for a billing-details error raised while the customer is *already on* the billing
 * form, where navigating them to it is not an option.
 *
 * `paymentErrorMessage` returns null for these codes because everywhere else the right answer is
 * to redirect. On the form itself that left the customer pressing a button and being told
 * nothing at all, so these two say what is actually missing.
 *
 * TODO: Danish copy to be reviewed by a native speaker before cutover.
 */
export const billingDetailsErrorMessage = (errorCode) => {
  switch (errorCode) {
    case PaymentError.BillingAddressInvalid:
      return "Udfyld alle faktureringsfelter, før du fortsætter.";
    case PaymentError.TermsVersionNotSpecified:
      return "Du skal acceptere betingelserne for at fortsætte.";
    default:
      return null;
  }
};
